import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import { usePrompt } from 'hooks/usePrompt';

import { AUTOCOMPLETE, SHIPPING_PARTNERS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

const defaultColSpan = {
	md: 6
};

const warehouseAndBranchAutocompleteColumns = [
	{ accessor: 'Name', width: 200 },
	{ accessor: 'ID', width: 150 }
];

const companyAutocompleteColumns = [
	{ accessor: 'ID', width: 200 },
	{ accessor: 'name', width: 800 }
];

const DCPickupLocationForm = NiceModal.create(({ id }) => {
	const modal = useModal();
	const dispatch = useDispatch();
	const axiosPrivate = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();

	const handleCloseModal = useCallback(() => {
		modal.remove();
	}, []);

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					companyID: yup.number().nullable().required(),
					warehouseID: yup.number().nullable(),
					branchID: yup.number().nullable(),
					dcPickupLocationId: yup.string().nullable().required(),
					dcSettingsId: yup.number().nullable().required()
				})
				.required(),
		[]
	);

	const fillForm = useCallback(async (id) => {
		if (!id) {
			return {
				companyID: null,
				warehouseID: null,
				branchID: null,
				dcPickupLocationId: null,
				dcSettingsId: null
			};
		}

		try {
			const response = await axiosPrivate.post(SHIPPING_PARTNERS.GET_DC_PICKUP_LOCATION, {
				id
			});

			const { data, status } = response;

			if (status !== 200) {
				AppLogger.error(response);
				showError();
				return;
			}

			return {
				companyID: data.companyId,
				warehouseID: data.warehouseId,
				branchID: data.branchId,
				dcPickupLocationId: data.dcPickupLocationId,
				dcSettingsId: data.dcSettingsId
			};
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	}, []);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: async () => await fillForm(id)
	});

	const {
		handleSubmit,
		setValue,
		watch,
		formState: { isDirty, isSubmitSuccessful, isSubmitting, isLoading: isFormLoading }
	} = formMethods;

	const isLoading = isFormLoading || isSubmitting;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);

	const onSubmit = (data) => {
		if (watch('branchID') === null && watch('warehouseID') === null) {
			showError({
				message: 'errorMsg.pleaseSelectWarehouseOrBranch'
			});
			return;
		}

		const url = id ? SHIPPING_PARTNERS.UPDATE_DC_PICKUP_LOCATION : SHIPPING_PARTNERS.ADD_DC_PICKUP_LOCATION;
		axiosPrivate
			.post(url, {
				id: id ?? undefined,
				companyId: data.companyID,
				warehouseId: data.warehouseID ?? undefined,
				branchId: data.branchID ?? undefined,
				integrationName: data.integrationName,
				dcPickupLocationId: data.dcPickupLocationId,
				dcSettingsId: data.dcSettingsId
			})
			.then((response) => {
				const { status } = response;

				if (status === 200) {
					dispatch(updateDynamicListData(DYNAMICLISTS.DC_PICKUP_LOCATION_LIST));
					showSuccess();
					handleCloseModal();
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				showError();
			});
	};

	return (
		<Modal fullwidth maxWidth="medium" className="modal-dc-pickup-location">
			<ModalTitle>
				<Typography variant="p1">{id ? 'editDcPickupLocationForm' : 'dcPickupLocationForm'}</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem colSpan={defaultColSpan} label="companies" isLoading={isLoading} required>
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.COMPANIES_FOR_DELIVERY_PRICE}
							name="companyID"
							value={watch('companyID')}
							setValue={(value) => {
								setValue('companyID', value?.ID ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							columns={companyAutocompleteColumns}
							selectionLabelAccessor={['ID', 'name']}
						/>
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="warehouse" isLoading={isLoading}>
						<AutoComplete
							name="warehouseID"
							autoCompleteName={AUTOCOMPLETE.WAREHOUSES_BY_COMPANY_BASED}
							columns={warehouseAndBranchAutocompleteColumns}
							selectionLabelAccessor={['ID', 'Name']}
							value={watch('warehouseID')}
							setValue={(value, isFirstRender) => {
								if (isFirstRender || value?.ID === watch('warehouseID')) return;

								setValue('warehouseID', value?.ID ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});

								if (value?.ID) {
									setValue('dcPickupLocationId', value?.ID);
									setValue('branchID', null);
								}
							}}
							disabled={watch('companyID') === null}
							filterObject={{
								userCompanyID: watch('companyID')
							}}
						/>
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="branch" isLoading={isLoading}>
						<AutoComplete
							name="branchID"
							autoCompleteName={AUTOCOMPLETE.BRANCHES_BY_COMPANY_BASED}
							columns={warehouseAndBranchAutocompleteColumns}
							selectionLabelAccessor={['ID', 'Name']}
							value={watch('branchID')}
							setValue={(value, isFirstRender) => {
								if (isFirstRender || value?.ID === watch('branchID')) return;

								setValue('branchID', value?.ID ?? null, { shouldValidate: true, shouldDirty: true });

								if (value?.ID) {
									setValue('warehouseID', null);
									setValue('dcPickupLocationId', value?.ID);
								}
							}}
							disabled={watch('companyID') === null}
							filterObject={{
								userCompanyID: watch('companyID')
							}}
						/>
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="dcSettingsID" isLoading={isLoading} required>
						<AutoComplete
							name="dcSettingsId"
							autoCompleteName={AUTOCOMPLETE.DELIVERY_COMPANY_SETTING_ALL_CLIENTS}
							columns={[
								{ accessor: 'ID', width: 90 },
								{ accessor: 'Name', width: 350 },
								{ accessor: 'ClientName', width: 350 },
								{ accessor: 'ClientID', width: 90 }
							]}
							selectionLabelAccessor={['ID', 'Name']}
							value={watch('dcSettingsId')}
							setValue={(value) => {
								setValue('dcSettingsId', value?.ID ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
								setValue('integrationName', value?.integrationName ?? null);
							}}
						/>
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="dcPickupLocationId" isLoading={isLoading} required>
						<StringInput name="dcPickupLocationId" />
					</FormItem>
				</Form>
			</ModalContent>

			<ModalActions>
				<Button label="cancel" variant="outline" onClick={handleCloseModal} />

				<Button
					loading={isLoading}
					label={id ? 'save' : 'button.add'}
					disabled={!isDirty}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
				/>
			</ModalActions>
		</Modal>
	);
});

export default DCPickupLocationForm;
